/**
 * One simulator session for the loaded element, wired to useTwin: readings go
 * through applyReadings, item and derived-status alerts through pushAlert,
 * failure stage changes and completions through pushEvent. Follows the store
 * for the interval, the active failure scenarios and the set of keys that
 * hold live readings (which the generator must not overwrite). Started and
 * stopped by TwinLiveRuntime.
 */
import { useTwin } from '../store';
import { isRfims } from '../../rfims/registry';
import type { Element } from '../types';
import { getDeriveStatusHook } from './deriveStatusHook';
import { createFailureController, type FailureController } from './failureScenarios';
import { createSimulation, liveKeysOf, type Simulation, type SimulationDeps } from './simulation';

export interface SimulationSession {
  stop(): void;
  simulation: Simulation;
  failures: FailureController;
}

const LEVEL_SEVERITY = { INFO: 'info', WARNING: 'warning', CRITICAL: 'critical' } as const;

export function startSimulationSession(element: Element, deps: SimulationDeps = {}): SimulationSession {
  if (isRfims(element.id)) throw new Error('RFIMS assets accept measured data only');
  const twin = useTwin.getState();

  const failures = createFailureController({
    onStageChanged: () => simulation.setFailureEffects(failures.currentEffects()),
    onStageAlert: (alert) =>
      useTwin.getState().pushEvent({
        severity: LEVEL_SEVERITY[alert.severity],
        title: alert.title,
        message: alert.message,
        equipment: alert.equipment,
      }),
    onCompleted: ({ scenarioId, totalUpdates }) =>
      useTwin.getState().pushEvent({
        severity: 'info',
        title: 'Failure scenario complete',
        message: `${scenarioId} ran its ${totalUpdates} updates; reset it to restore normal operation`,
      }),
    onStopped: (scenarioId) => {
      simulation.setFailureEffects(failures.currentEffects());
      useTwin.getState().pushEvent({ severity: 'info', title: 'Failure scenario stopped', message: `${scenarioId} cleared` });
    },
  });

  const simulation = createSimulation(
    {
      element,
      intervalMs: twin.simIntervalMs,
      initialReadings: twin.readings,
      deriveStatus: getDeriveStatusHook(),
      onReadings: (readings) => useTwin.getState().applyReadings(readings),
      onAlert: (alert) => useTwin.getState().pushAlert(alert),
      onCycleComplete: () => failures.onCycleComplete(),
    },
    deps,
  );

  // Scenarios already selected when the session starts.
  for (const id of twin.failureScenarios) failures.start(id);
  simulation.setFailureEffects(failures.currentEffects());

  let liveKeys = liveKeysOf(twin.readings).join('\n');
  const unsubscribe = useTwin.subscribe((state, prev) => {
    if (state.simIntervalMs !== prev.simIntervalMs) simulation.setIntervalMs(state.simIntervalMs);
    if (state.failureScenarios !== prev.failureScenarios) {
      const wanted = new Set(state.failureScenarios);
      for (const active of failures.getActive()) if (!wanted.has(active.scenarioId)) failures.stop(active.scenarioId);
      for (const id of wanted) if (!failures.isActive(id)) failures.start(id);
    }
    if (state.readings !== prev.readings) {
      const next = liveKeysOf(state.readings).join('\n');
      if (next !== liveKeys) {
        liveKeys = next;
        simulation.setExcludedKeys(next ? next.split('\n') : []);
      }
    }
  });

  simulation.start();

  return {
    stop() {
      unsubscribe();
      simulation.stop();
    },
    simulation,
    failures,
  };
}
